import React, { useContext, useEffect, useMemo } from 'react';
import { Route, useLocation } from 'react-router-dom';
import {
  IonIcon,
  IonRouterOutlet,
  IonSplitPane,
  IonTabBar,
  IonTabButton,
  IonTabs,
  NavContext,
} from '@ionic/react';
import {
  globeOutline,
  heartOutline,
  notificationsOutline,
  settingsOutline,
} from 'ionicons/icons';
import { SubjectGeneric, subjectsSlice } from '../redux/slices/subjects/slice';
import { useTypedDispatch } from '../redux/hooks';
import PrivateRoute from '../components/PrivateRoute/PrivateRoute';
import { AppMenu } from './AppMenu';
import { AppConfigType } from './types';
import clsx from 'clsx';
import { isUserFormPage } from './helpers';
import { AppTabLogo } from './AppTabLogo';
import { IS_IOS_NATIVE, IS_IOS_PWA } from '../utils/browser';
import { useIsBigScreen } from '../hooks/hook.big-screen';
import UserCatalogRoutes from '../pages/user/catalog/UserCatalogRoutes';
import UserFavouritesRoutes from '../pages/user/favourites/UserFavouritesRoutes';
import UserEventsRoutes from '../pages/user/events/UserEventsRoutes';
import UserNotificationsRoutes from '../pages/user/notifications/UserNotificationsRoutes';
import UserSettingsRoutes from '../pages/user/settings/UserSettingsRoutes';
import UserAssistancePage from '../pages/user/catalog/UserAssistancePage';
import { useLocationRedirector } from '../pages/user/catalog/hook.useLocationRedirector';

const AppUser: React.FC = () => {
  const location = useLocation();
  const pathName = location.pathname;

  const flow = useMemo(() => {
    const search = new URLSearchParams(location.search);
    return search.get('flow');
  }, [location.search]);

  const dispatch = useTypedDispatch();
  useEffect(() => {
    const activeSubject: SubjectGeneric = {
      contentType: 'user',
      objectId: undefined,
    };
    dispatch(subjectsSlice.actions.setActiveSubject(activeSubject));
  }, [dispatch]);

  const { isBigScreen } = useIsBigScreen('900px');

  const { routeInfo } = useContext(NavContext);

  // const [lastCatalogSearch, setLastCatalogSearch] = useState('');
  // const [lastCatalogPathname, setLastCatalogPathname] = useState('/catalog');

  // useEffect(() => {
  //   if (
  //     !!routeInfo &&
  //     !!routeInfo.lastPathname &&
  //     !routeInfo.pathname.startsWith('/catalog') &&
  //     routeInfo.lastPathname.startsWith('/catalog')
  //   ) {
  //     setLastCatalogPathname(routeInfo.lastPathname);
  //   }

  //   if (!!routeInfo && routeInfo.pathname.match(/^\/catalog\/([^/]+)$/)) {
  //     setLastCatalogSearch(routeInfo.search);
  //   }
  // }, [routeInfo]);

  // Temporary
  const catalogLink = useLocationRedirector({ allowSet: false }) || '/catalog';
  const catalogMiniLink = catalogLink.split('?')[0];

  const config: AppConfigType = useMemo(() => {
    return [
      {
        link: '/catalog',
        name: 'Catalog',
        icon: globeOutline,
      },
      {
        link: '/favourites',
        name: 'Saved',
        icon: heartOutline,
      },
      {
        link: '/events',
        name: 'My Events',
        icon: 'custom',
      },
      {
        link: '/notifications',
        name: 'Notifications',
        icon: notificationsOutline,
      },
      {
        link: '/settings',
        name: 'Settings',
        icon: settingsOutline,
      },
    ];
  }, []);

  const isAssistancePage = pathName === '/assistance';
  const isFormPage = useMemo(() => isUserFormPage(pathName), [pathName]);
  const isCentralTabActive = pathName.startsWith(config[2].link);

  return isBigScreen ? (
    <IonSplitPane contentId="main" when="xs">
      {flow !== '1' && (
        <AppMenu config={config} isFormPage={isFormPage} pathName={pathName} />
      )}

      <IonRouterOutlet id="main" animated={false} mode="md" basePath="/">
        <Route path="/catalog">
          <UserCatalogRoutes />
        </Route>

        <Route exact path="/assistance">
          <UserAssistancePage />
        </Route>

        <PrivateRoute path="/favourites">
          <UserFavouritesRoutes />
        </PrivateRoute>

        <PrivateRoute path="/events">
          <UserEventsRoutes />
        </PrivateRoute>

        <PrivateRoute path="/notifications">
          <UserNotificationsRoutes />
        </PrivateRoute>

        <PrivateRoute path="/settings">
          <UserSettingsRoutes />
        </PrivateRoute>
      </IonRouterOutlet>
    </IonSplitPane>
  ) : (
    <IonTabs>
      <IonRouterOutlet ionPage>
        <Route path="/catalog">
          <UserCatalogRoutes />
        </Route>

        <Route path="/assistance">
          <UserAssistancePage />
        </Route>

        <PrivateRoute path="/favourites">
          <UserFavouritesRoutes />
        </PrivateRoute>

        <PrivateRoute path="/events">
          <UserEventsRoutes />
        </PrivateRoute>

        <PrivateRoute path="/notifications">
          <UserNotificationsRoutes />
        </PrivateRoute>

        <PrivateRoute path="/settings">
          <UserSettingsRoutes />
        </PrivateRoute>
      </IonRouterOutlet>

      <IonTabBar
        id="ionTabBar"
        slot="bottom"
        mode="ios"
        className={clsx(
          (IS_IOS_NATIVE || IS_IOS_PWA) && 'fillColorToBottom',
          'disableIosSafariSwipeBlocker'
        )}
        style={isFormPage || isAssistancePage ? { display: 'none' } : undefined}
      >
        {flow !== '1' &&
          config.map(cfg =>
            cfg.icon !== 'custom' ? (
              <IonTabButton
                key={cfg.name}
                tab={cfg.name}
                className={'disableIosSafariSwipeBlocker'}
                href={cfg.link}
                //
                // Workaround to fix router when click on active "/catalog" tab
                disabled={
                  cfg.link === '/catalog' &&
                  !!routeInfo?.pathname.startsWith('/catalog')
                }
                style={{ opacity: 1, pointerEvents: 'unset' }}
                // Workaround
              >
                <IonIcon
                  icon={cfg.icon}
                  className="disableIosSafariSwipeBlocker"
                />
              </IonTabButton>
            ) : (
              <IonTabButton
                key={cfg.name}
                tab={cfg.name}
                href={cfg.link}
                className="centralTab"
              >
                <AppTabLogo isTabActive={isCentralTabActive} />
              </IonTabButton>
            )
          )}
      </IonTabBar>
    </IonTabs>
  );
};

export default AppUser;
